import { z } from 'zod';
import { CursorInputSchema, IdSchema, IsoDateTimeSchema, pageOf, withMeta } from '../common';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { ChannelDescriptorSchema, ChannelKindSchema, WsTicketSchema } from '../realtime';
import { defineFamily } from './family';

/** engine.* — rule-engine status/heartbeat (R-07 panel on Risk Rules; TradeHub gates) and the realtime channel descriptors
 *  (SDD §14.1). The trade engine runs a 7s loop under a lease and fails closed: a lease-renew error skips the tick. */

export const EngineStatusSchema = withMeta({
  status: z.enum(['running', 'degraded', 'stopped']),
  lastTickAt: IsoDateTimeSchema,
  tickIntervalS: z.number().int(),
  lease: z.object({ holder: z.string(), renewedAt: IsoDateTimeSchema, failClosed: z.literal(true) }).strict(),
  breaker: z.enum(['closed', 'half_open', 'open']),
  /** Evaluator mode: fail-closed means DB unreachable ⇒ refuse orders (§13.4). */
  evaluator: z.object({ healthy: z.boolean(), failClosed: z.literal(true) }).strict(),
  activeRules: z.number().int(),
  /** Kill switches the engine honours (dev console / admin flags). */
  killSwitches: z.record(z.string(), z.boolean()),
});
export type EngineStatus = z.infer<typeof EngineStatusSchema>;
export const ActionFireSchema = z
  .object({
    id: IdSchema,
    ruleKey: z.string(),
    action: z.string(),
    firedAt: IsoDateTimeSchema,
    detail: z.string(),
    sessionId: IdSchema.nullable(),
  })
  .strict();

export const engineProcedures = defineProcedures({
  'engine.status.get': query('Rule-engine status panel (R-07)', z.void(), EngineStatusSchema),
  'engine.status.heartbeat': query(
    'Lightweight heartbeat (5s session channel mirror)',
    z.void(),
    withMeta({ alive: z.boolean(), at: IsoDateTimeSchema, seq: z.number().int() }),
  ),
  'engine.actions.list': query(
    'Automation action fires (immutable)',
    CursorInputSchema.optional(),
    pageOf(ActionFireSchema),
  ),
  'engine.realtime.channels': query(
    'The five realtime channel descriptors (§14.1)',
    z.void(),
    withMeta({ channels: z.array(ChannelDescriptorSchema) }),
  ),
  'engine.realtime.ticket': mutation(
    'Issue a 60s device-bound WS ticket authorising channel access',
    z
      .object({
        deviceId: IdSchema,
        channels: z.array(ChannelKindSchema).min(1),
        /** Price symbols to authorise beyond the watchlist — the chart's current instrument need not be watched.
         *  Bounded, because a ticket is a fan-out grant and an unbounded list is a fan-out amplifier. */
        symbols: z.array(z.string().min(3).max(20)).max(8).optional(),
      })
      .strict(),
    WsTicketSchema,
  ),
});
export type EngineFamilyClient = RouterShape<typeof engineProcedures>;

export const engineFamily = defineFamily({
  name: 'engine',
  title: 'Engine & realtime',
  screens: ['Risk Rules', 'TradeHub', 'Inherited baseline (realtime channels)'],
  procedures: engineProcedures,
  binding: 'simulated',
});
